import { motion } from "framer-motion";
import { ArrowRight, Quote, Newspaper, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Eyebrow } from "@/components/landing/Eyebrow";
import { FadeUp } from "@/components/landing/Motion";
import { BrandMarquee } from "@/components/landing/BrandMarquee";
import { SPEAKER, SPEAKER_STATS, WEBINAR, SCROLL_REVEAL } from "@/lib/content";

/* Celebrities collaborated — name-only layout */
const CELEBRITIES = [
  { name: "陳奕迅", role: "Eason Chan" },
  { name: "古天樂", role: "Louis Koo" },
  { name: "張家輝", role: "Nick Cheung" },
  { name: "吳鎮宇", role: "Francis Ng" },
  { name: "林 峯", role: "Raymond Lam" },
  { name: "容祖兒", role: "Joey Yung" },
  { name: "Twins", role: "蔡卓妍・鍾欣潼" },
];

/* Brands organized by category (text-only, logos coming) */
const BRAND_CATEGORIES = [
  { cat: "金融・理財", items: ["Alipay", "Standard Chartered", "安信 PrimeCredit"] },
  { cat: "科技・娛樂", items: ["Samsung", "PlayStation", "MOOV", "reenex"] },
  { cat: "交通・航空", items: ["Toyota", "Audi", "UBER", "HK Airlines", "Trip.com", "Booking.com"] },
  { cat: "飲食・消費", items: ["Coca-Cola", "Carlsberg", "San Miguel", "Pabst Blue Ribbon", "Kjeldsens"] },
  { cat: "旅遊・文娛", items: ["HK Disneyland", "百老匯", "MGM 美高梅", "MX", "tictac"] },
  { cat: "政府・公共", items: ["CTM 澳門電訊", "澳門消防", "香港寬頻"] },
];

export const SpeakerCredentials = ({ onCtaClick }) => {
  return (
    <section
      id="speaker"
      data-testid="speaker-credentials-section"
      className="relative bg-ink py-14 sm:py-24 lg:py-28"
    >
      <div className="container px-4 sm:px-6 lg:px-8 max-w-6xl mx-auto">
        {/* ===== HERO ROW: Portrait + Intro ===== */}
        <div className="grid md:grid-cols-12 gap-6 sm:gap-10 lg:gap-14 items-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="md:col-span-5 relative"
          >
            <div className="relative rounded-2xl overflow-hidden border border-[rgba(212,180,106,0.22)] shadow-[0_25px_60px_rgba(0,0,0,0.6),0_0_0_1px_rgba(212,180,106,0.12)_inset]">
              <img src="/speaker/processed/portrait.jpg" alt={SPEAKER.name} className="w-full h-auto object-cover" loading="lazy" />
              <div className="absolute inset-0 pointer-events-none" style={{ background: "linear-gradient(135deg, transparent 60%, rgba(212,180,106,0.08))" }} />
            </div>
          </motion.div>

          <motion.div {...SCROLL_REVEAL} className="md:col-span-7">
            <Eyebrow label="SPEAKER" sub="CREDENTIALS" />
            <h2 className="font-serif-tc mt-5 sm:mt-7 text-[28px] leading-[1.15] sm:text-5xl lg:text-[56px] font-bold tracking-[-0.02em]">
              {SPEAKER.name}｜<span className="lime-keyword">{SPEAKER.title}</span>
            </h2>
            <p className="mt-4 sm:mt-6 text-[14px] sm:text-base leading-relaxed text-[hsl(var(--muted-foreground))] max-w-[60ch]">
              {SPEAKER.bio}
            </p>
            <div className="mt-5 sm:mt-6 flex flex-wrap gap-2">
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[rgba(212,180,106,0.08)] border border-[rgba(212,180,106,0.28)] text-[11px] sm:text-[12px] text-[hsl(var(--foreground))]/90">
                <span className="w-1.5 h-1.5 rounded-full bg-[hsl(var(--lime))]" />
                曾操盤頂級 IP 媒體
              </span>
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[rgba(212,180,106,0.08)] border border-[rgba(212,180,106,0.28)] text-[11px] sm:text-[12px] text-[hsl(var(--foreground))]/90">
                <span className="w-1.5 h-1.5 rounded-full bg-[hsl(var(--lime))]" />
                10 億+ 累積觀看
              </span>
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[rgba(212,180,106,0.08)] border border-[rgba(212,180,106,0.28)] text-[11px] sm:text-[12px] text-[hsl(var(--foreground))]/90">
                <span className="w-1.5 h-1.5 rounded-full bg-[hsl(var(--lime))]" />
                千萬年收入實戰
              </span>
            </div>
          </motion.div>
        </div>

        {/* ===== STATS GRID ===== */}
        <div className="mt-12 sm:mt-20 grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-5">
          {SPEAKER_STATS.map((s, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.45, delay: i * 0.04 }}
              data-testid={`speaker-stat-${i}`}
              className="card-dark p-5 sm:p-7"
            >
              <div className="flex items-baseline gap-1 whitespace-nowrap">
                <div className="stat-num text-[26px] sm:text-5xl lg:text-[56px] text-[hsl(var(--foreground))]">{s.num}</div>
                <div className="text-[11px] sm:text-sm font-medium text-[hsl(var(--muted-foreground))]">{s.unit}</div>
              </div>
              <div className="mt-3 sm:mt-4 text-[12px] sm:text-[15px] text-[hsl(var(--foreground))]/85 tracking-wide">{s.label}</div>
            </motion.div>
          ))}
        </div>

        {/* ===== HKET NEWS — FULL IMAGE ===== */}
        <FadeUp delay={0.05}>
          <div className="mt-16 sm:mt-24">
            <div className="flex items-center gap-2.5 mb-5">
              <div className="h-px w-8 bg-[rgba(212,180,106,0.5)]" />
              <span className="text-[10px] sm:text-[11px] uppercase tracking-[0.22em] font-semibold text-[hsl(var(--lime))]">
                Part 01 / Media Featured
              </span>
            </div>
            <div className="card-dark relative p-5 sm:p-8">
              <div className="flex items-center gap-3 mb-4">
                <Quote className="w-8 h-8 text-[hsl(var(--lime))]/40 flex-shrink-0" />
                <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-[rgba(212,180,106,0.1)] border border-[rgba(212,180,106,0.3)] text-[10px] uppercase tracking-[0.2em] text-[hsl(var(--lime))] font-bold">
                  <Newspaper className="w-3 h-3" /> HKET TOP!CK · 商業頭條
                </div>
              </div>
              <div className="rounded-lg overflow-hidden border border-[rgba(255,255,255,0.08)] bg-white">
                <img
                  src="/speaker/processed/hket-full.jpg"
                  alt="HKET news article"
                  className="w-full h-auto"
                  loading="lazy"
                />
              </div>
              <div className="mt-4 text-[11px] sm:text-[12px] text-[hsl(var(--muted-foreground))]">
                商業頭條 · 2018/01/02 · 節錄報導
              </div>
            </div>
          </div>
        </FadeUp>

        {/* ===== CELEBRITY NAMES (text-only cards, premium) ===== */}
        <FadeUp delay={0.05}>
          <div className="mt-16 sm:mt-20">
            <div className="flex items-center gap-2.5 mb-3">
              <div className="h-px w-8 bg-[rgba(212,180,106,0.5)]" />
              <span className="text-[10px] sm:text-[11px] uppercase tracking-[0.22em] font-semibold text-[hsl(var(--lime))]">
                Part 02 / Talent Collaborations
              </span>
            </div>
            <h3 className="font-serif-tc text-[22px] sm:text-3xl text-[hsl(var(--foreground))] font-bold mb-2">
              曾合作的<span className="lime-keyword">藝人嘉賓</span>
            </h3>
            <p className="text-[12px] sm:text-[13px] text-[hsl(var(--muted-foreground))] mb-6 sm:mb-8 flex items-center gap-1.5">
              <Users className="w-3.5 h-3.5" />
              於過往操盤的內容項目中，曾與以下華語頂級藝人進行專訪 / 演出合作：
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2 sm:gap-3">
              {CELEBRITIES.map((c, i) => (
                <motion.div
                  key={c.name}
                  initial={{ opacity: 0, y: 15 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.45, delay: i * 0.05 }}
                  whileHover={{ y: -3 }}
                  className="group text-center py-5 sm:py-7 px-3 rounded-xl border border-[rgba(212,180,106,0.18)] bg-gradient-to-b from-[rgba(212,180,106,0.04)] to-transparent hover:border-[rgba(212,180,106,0.5)] hover:bg-[rgba(212,180,106,0.08)] transition-all duration-300"
                  data-testid={`celeb-${i}`}
                >
                  <div className="font-serif-tc text-[17px] sm:text-[20px] text-[hsl(var(--foreground))] font-bold tracking-[0.04em] group-hover:text-[hsl(var(--lime))] transition-colors">
                    {c.name}
                  </div>
                  <div className="text-[9.5px] sm:text-[10.5px] uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))] mt-1">
                    {c.role}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </FadeUp>

        {/* ===== BRAND LOGO MARQUEE ===== */}
        <FadeUp delay={0.05}>
          <BrandMarquee />
        </FadeUp>

        {/* ===== CTA ===== */}
        <div className="mt-12 sm:mt-20 flex">
          <Button
            data-testid="webinar-cta-stats-button"
            onClick={onCtaClick}
            size="lg"
            className="btn-lime btn-lime-pulse w-full sm:w-auto h-14 px-7 text-[16px] font-bold"
          >
            {WEBINAR.ctaCopy}
            <ArrowRight className="w-4 h-4 ml-1.5" />
          </Button>
        </div>
      </div>
    </section>
  );
};
